import { Input, Stack, Textarea } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import * as React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import {
  createCustomerProfileSubset,
  updateCustomerProfileSubset,
} from "../api/company-knowledge";
import { useLoading, useToaster } from "../hooks";
import { CustomerProfileSubset } from "../types/company-knowledge";
import { Button } from "./button";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "./dialog";
import { Field } from "./field";
import { toaster } from "./toaster";

// A subset passes `subset` to edit an existing one, or null to create.
// Field names mirror the ideal customer profile's (subsets inherit it
// and only describe what is more specific), so the labels match the
// ICP page's.

const customerProfileSubsetSchema = z.object({
  name: z.string().trim().min(1, "A name is required."),
  description: z.string(),
  "business-models": z.string(),
  industries: z.string(),
  "company-size": z.string(),
  geographies: z.string(),
  "buyer-roles": z.string(),
  "buyer-needs": z.string(),
  disqualifiers: z.string(),
});

type CustomerProfileSubsetFormData = z.infer<
  typeof customerProfileSubsetSchema
>;

interface CustomerProfileSubsetDialogProps {
  isOpen: boolean;
  onOpenChange: (details: { open: boolean }) => void;
  onSuccess: () => void;
  subset?: CustomerProfileSubset | null;
  workspaceSlug: string;
}

export function CustomerProfileSubsetDialog({
  isOpen,
  onOpenChange,
  onSuccess,
  subset,
  workspaceSlug,
}: CustomerProfileSubsetDialogProps) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [isLoading, withLoading] = useLoading();
  const withToaster = useToaster();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CustomerProfileSubsetFormData>({
    resolver: zodResolver(customerProfileSubsetSchema),
    defaultValues: emptySubset,
  });

  React.useEffect(() => {
    if (!isOpen) return;
    reset(subset ? toFormData(subset) : emptySubset);
  }, [isOpen, reset, subset]);

  const onSubmit = async (data: CustomerProfileSubsetFormData) => {
    await withLoading(async () => {
      await withToaster(async () => {
        if (subset) {
          await updateCustomerProfileSubset(workspaceSlug, subset.id, data);
          toaster.create({ type: "success", title: "ICP subset updated" });
        } else {
          await createCustomerProfileSubset(workspaceSlug, data);
          toaster.create({ type: "success", title: "ICP subset created" });
        }
        onOpenChange({ open: false });
        onSuccess();
      });
    });
  };

  return (
    <DialogRoot open={isOpen} onOpenChange={onOpenChange} size="md">
      <DialogContent ref={ref}>
        <DialogCloseTrigger />
        <DialogHeader>
          <DialogTitle>
            {subset ? "Edit ICP Subset" : "Create ICP Subset"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogBody paddingBottom="6">
            <Stack gap="4">
              <Field
                label="Name"
                invalid={!!errors.name}
                errorText={errors.name?.message}
              >
                <Input
                  size="sm"
                  placeholder="e.g. Managed service providers"
                  {...register("name")}
                />
              </Field>
              <Field
                label="Describe this subset"
                helperText="Subsets inherit the overall ICP; describe only what is more specific."
                invalid={!!errors.description}
                errorText={errors.description?.message}
              >
                <Textarea
                  {...register("description")}
                  placeholder="What defines this segment of your ideal customer."
                  rows={3}
                />
              </Field>
              <Field
                label="Company types and business models"
                invalid={!!errors["business-models"]}
                errorText={errors["business-models"]?.message}
              >
                <Textarea {...register("business-models")} rows={2} />
              </Field>
              <Field
                label="Industries"
                invalid={!!errors.industries}
                errorText={errors.industries?.message}
              >
                <Textarea {...register("industries")} rows={2} />
              </Field>
              <Field
                label="Company size"
                helperText="Free text, e.g. headcount or revenue band."
                invalid={!!errors["company-size"]}
                errorText={errors["company-size"]?.message}
              >
                <Input {...register("company-size")} />
              </Field>
              <Field
                label="Geographies"
                helperText="Where these customers are, not where campaigns run."
                invalid={!!errors.geographies}
                errorText={errors.geographies?.message}
              >
                <Input {...register("geographies")} />
              </Field>
              <Field
                label="Buyer roles and responsibilities"
                invalid={!!errors["buyer-roles"]}
                errorText={errors["buyer-roles"]?.message}
              >
                <Textarea {...register("buyer-roles")} rows={2} />
              </Field>
              <Field
                label="Needs and buying situations"
                invalid={!!errors["buyer-needs"]}
                errorText={errors["buyer-needs"]?.message}
              >
                <Textarea {...register("buyer-needs")} rows={2} />
              </Field>
              <Field
                label="Who is not a fit?"
                invalid={!!errors.disqualifiers}
                errorText={errors.disqualifiers?.message}
              >
                <Textarea {...register("disqualifiers")} rows={2} />
              </Field>
            </Stack>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => onOpenChange({ open: false })}
            >
              Cancel
            </Button>
            <Button loading={isLoading} type="submit">
              {subset ? "Save Changes" : "Create Subset"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </DialogRoot>
  );
}

const emptySubset: CustomerProfileSubsetFormData = {
  name: "",
  description: "",
  "business-models": "",
  industries: "",
  "company-size": "",
  geographies: "",
  "buyer-roles": "",
  "buyer-needs": "",
  disqualifiers: "",
};

// The schema's fields are nullable strings; empty strings are the
// form's representation of "unset".
const toFormData = (
  subset: CustomerProfileSubset,
): CustomerProfileSubsetFormData => ({
  name: subset.name ?? "",
  description: subset.description ?? "",
  "business-models": subset["business-models"] ?? "",
  industries: subset.industries ?? "",
  "company-size": subset["company-size"] ?? "",
  geographies: subset.geographies ?? "",
  "buyer-roles": subset["buyer-roles"] ?? "",
  "buyer-needs": subset["buyer-needs"] ?? "",
  disqualifiers: subset.disqualifiers ?? "",
});
